import { useState, useEffect, useCallback, useRef, DependencyList } from 'react';

interface UseQueryResult<T> {
  data: T | null;
  /** True only on the very first fetch (no data yet). */
  isLoading: boolean;
  /** True during any fetch, including refetches after a dep change. */
  isFetching: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Stale-while-revalidate fetch hook.
 *
 * Default behavior:
 *   - Always fetches on mount and when deps change.
 *   - Keeps the previous result visible during refetch — no blank flash when
 *     the user changes a filter or navigates back to this page.
 *   - `isLoading` means "nothing to show yet." `isFetching` means "a request
 *     is in flight." Use `isLoading` for full-page skeletons; ignore
 *     `isFetching` unless you specifically want a mid-fetch indicator.
 *
 * Opt-in caching: pass a unique `cacheKey` to share results across remounts
 * within `ttlMs`. The key must uniquely identify the *endpoint + arguments*,
 * not just the arguments (otherwise two different endpoints with the same
 * deps shape would collide). Example: `{ cacheKey: \`scorecard:\${userId}:\${months}\` }`.
 */
interface CacheEntry { data: unknown; expires: number }
const cache = new Map<string, CacheEntry>();
const DEFAULT_TTL_MS = 15_000;

export function useQuery<T>(
  fn: () => Promise<T>,
  deps: DependencyList = [],
  options: { cacheKey?: string; ttlMs?: number } = {},
): UseQueryResult<T> {
  const { cacheKey, ttlMs = DEFAULT_TTL_MS } = options;

  const initial = (() => {
    if (!cacheKey) return null;
    const hit = cache.get(cacheKey);
    return hit && hit.expires > Date.now() ? (hit.data as T) : null;
  })();

  const [data, setData] = useState<T | null>(initial);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const lastFn = useRef(fn);
  lastFn.current = fn;

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setIsFetching(true);
    setError(null);
    lastFn.current()
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setIsFetching(false);
        if (cacheKey) cache.set(cacheKey, { data: result, expires: Date.now() + ttlMs });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
        setIsFetching(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return {
    data,
    isLoading: data === null && isFetching,
    isFetching,
    error,
    refetch,
  };
}
